import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "./cn";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "success";
export type ButtonSize = "sm" | "md";

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-accent text-ink-on-accent hover:bg-accent-hover border border-transparent",
  secondary:
    "bg-surface text-ink border border-line hover:bg-surface-sunk",
  ghost:
    "bg-transparent text-ink-muted hover:bg-surface-sunk hover:text-ink border border-transparent",
  danger:
    "bg-risk text-ink-on-accent hover:brightness-110 border border-transparent",
  success:
    "bg-ok text-ink-on-accent hover:brightness-110 border border-transparent",
};

const SIZES: Record<ButtonSize, string> = {
  // 44px min height on touch targets; the compact 32px is desktop-only.
  sm: "h-8 px-2.5 text-[13px] gap-1.5 max-md:h-11 max-md:px-4",
  md: "h-9 px-3.5 text-sm gap-2 max-md:h-11",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", icon, loading, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap",
        "transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        "disabled:opacity-50 disabled:pointer-events-none",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
});

function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}
